import React from 'react';

import {checkDataModsReasonRequired, checkFolderMoveAllowedByDataMods} from 'audit/AuditDAO';
import ReasonCodeAndCommentModal, {ReasonCodeAndCommentModalProps} from 'audit/ReasonCodeAndCommentModal';
import BenchlingConfig from 'BenchlingConfig';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import legacyRunModal from 'ui-library/legacyRunModal';
import {OptionData} from 'ui-library/menus/MenuConstants';
import uniqueId from 'util/uniqueId';

import {
  areDataModsEnabledAnywhere,
  DataModificationsHeader,
  ReasonCodeAndCommentType,
} from './DataModifications';
import {DataModificationsAnalayticsParams} from './DataModificationsTracking';

const DATA_MODS_MODAL_KEY_PREFIX = 'DATA_MODS_MODAL_KEY';

export const DATA_MODS_MODAL_TITLE = 'Provide a reason for the change';

// const props that are set inside DataModificationsModal
interface ConstProps {
  modalKeyPrefix: string;
  modalTitle: string;
  isCommentOptional: boolean;
  reasonCodes: () => Array<OptionData>;
  dataTestAttrs: DataTestAttrs;
  reasonCodeAndCommentType: ReasonCodeAndCommentType;
}

export type DataModificationsModalProps = Omit<ReasonCodeAndCommentModalProps, keyof ConstProps> & {
  additionalReasonCodeAndComments?: DataModificationsHeader;
};

export interface CallWithDataModificationsArgs {
  changeSummary?: string | JSX.Element;
  onPerformAction: (dataModsHeader?: DataModificationsHeader) => Promise<void> | void;
  onModalCancel?: () => void;
  apiIds: Array<string>;
  analyticsParams: DataModificationsAnalayticsParams;
  useProjectLevelDataModsLogic: boolean;
  useFolderMoveDataModsLogic?: {
    apiIds: Array<string>;
    destinationFolderApiId: string;
  };
  useDataModsIfEnabledAnywhere?: boolean;
  additionalReasonCodeAndComments?: DataModificationsHeader;
}

/**
 * Wrapper for calling synchronous methods with a data modifications modal.
 * Note, if running logic sequentially after this method, will need to await this function even though the
 * method being wrapped is synchronous as the checkDataModsReasonRequired method is async
 *
 * @param changeSummary string or JSX.Element summarizing what edits the user is making
 * @param onPerformAction function to call when the modal is submitted. The user action that requires data mods
 * @param onModalCancel function to call when the user cancels out of the modal
 * @param analyticsParams DataModificationsTrackingParams for product analytics
 * @param apiIds Array of api ids to check for data mods reason required
 * @param useProjectLevelDataModsLogic boolean feature flag to use the new project-level logic for displaying the modal
 * @param useFolderMoveDataModsLogic object used only when enforcing data mods for folder moves
 * @param useDataModsIfEnabledAnywhere boolean to control showing data mods if they are enabled anywhere on
 *        the tenant rather than checking specific api ids. Relevant only when using project level data mods,
 *        useProjectLevelDataModsLogic=true.
 * @param additionalReasonCodeAndComments optional reason code and comments to include from other modals
 *        that may have been shown before data mods
 */
export async function callWithDataModifications({
  changeSummary = '',
  onPerformAction,
  onModalCancel,
  analyticsParams,
  apiIds,
  useProjectLevelDataModsLogic = false,
  useFolderMoveDataModsLogic,
  useDataModsIfEnabledAnywhere = false,
  additionalReasonCodeAndComments,
}: CallWithDataModificationsArgs): Promise<void> {
  let skipModal = true;
  if (useFolderMoveDataModsLogic) {
    // needed for edge-case where item does not have data mods but will *after* folder move
    skipModal = !(
      await checkFolderMoveAllowedByDataMods(
        useFolderMoveDataModsLogic.apiIds,
        useFolderMoveDataModsLogic.destinationFolderApiId
      )
    ).data_mods_reason_required;
  } else if (useProjectLevelDataModsLogic) {
    if (useDataModsIfEnabledAnywhere) {
      skipModal = !areDataModsEnabledAnywhere();
    } else {
      skipModal = !(await checkDataModsReasonRequired(apiIds))?.data_mods_reason_required;
    }
  }

  if (skipModal) {
    onPerformAction(additionalReasonCodeAndComments);
    return;
  }

  legacyRunModal(DataModificationsModal, {
    changeSummary,
    onRequestClose: onModalCancel,
    onRequestSubmit: onPerformAction,
    modalKey: uniqueId(DATA_MODS_MODAL_KEY_PREFIX),
    analyticsParams,
    additionalReasonCodeAndComments,
  });
}

/**
 * Wrapper for calling synchronous methods with a data modifications modal.
 *
 * @param changeSummary string or JSX.Element summarizing what edits the user is making
 * @param onPerformAction function to call when the modal is submitted. The user action that requires data mods
 * @param onModalCancel function to call when the user cancels out of the modal
 * @param analyticsParams DataModificationsTrackingParams for product analytics
 * @param apiIds Array of api ids to check for data mods reason required
 * @param useProjectLevelDataModsLogic boolean feature flag to use the new project-level logic for displaying the modal
 * @param useFolderMoveDataModsLogic object used only when enforcing data mods for folder moves
 * @param useDataModsIfEnabledAnywhere boolean to control showing data mods if they are enabled anywhere on
 *        the tenant rather than checking specific api ids. Relevant only when using project level data mods,
 *        useProjectLevelDataModsLogic=true.
 * @param additionalReasonCodeAndComments optional reason code and comments to include from other modals
 *        that may have been shown before data mods
 * @return Promise resolving `true` if the modal is submitted, `false` if it is closed without submitting (by clicking the "x")
 */
export async function callAsyncWithDataModifications({
  changeSummary = '',
  onPerformAction,
  onModalCancel,
  analyticsParams,
  apiIds,
  useProjectLevelDataModsLogic = false,
  useFolderMoveDataModsLogic,
  useDataModsIfEnabledAnywhere = false,
  additionalReasonCodeAndComments,
}: CallWithDataModificationsArgs): Promise<boolean> {
  let skipModal = true;
  if (useFolderMoveDataModsLogic) {
    // needed for edge-case where item does not have data mods but will *after* folder move
    skipModal = !(
      await checkFolderMoveAllowedByDataMods(
        useFolderMoveDataModsLogic.apiIds,
        useFolderMoveDataModsLogic.destinationFolderApiId
      )
    ).data_mods_reason_required;
  } else if (useProjectLevelDataModsLogic) {
    if (useDataModsIfEnabledAnywhere) {
      skipModal = !areDataModsEnabledAnywhere();
    } else {
      skipModal = !(await checkDataModsReasonRequired(apiIds))?.data_mods_reason_required;
    }
  }

  if (skipModal) {
    await onPerformAction(additionalReasonCodeAndComments);
    return true;
  }

  let submitted = false;
  await legacyRunModal(DataModificationsModal, {
    changeSummary,
    onRequestClose: onModalCancel,
    onRequestSubmit: async (dataModsHeader: DataModificationsHeader) => {
      await onPerformAction(dataModsHeader);
      // `submitted` must be set after the action succeeds. If the action throws an error, the modal
      // stays open for a retry, and a later close must report that nothing was submitted.
      submitted = true;
    },
    modalKey: uniqueId(DATA_MODS_MODAL_KEY_PREFIX),
    analyticsParams,
    additionalReasonCodeAndComments,
  });

  return submitted;
}

export default function DataModificationsModal(props: DataModificationsModalProps): JSX.Element {
  function _getReasonCodes(): Array<OptionData> {
    const reasonCodes = BenchlingConfig.AUDITING_DATA_MODIFICATIONS_REASONS_FOR_CHANGE;
    return reasonCodes.map((reasonCode: string) => ({
      name: reasonCode,
      value: reasonCode,
    }));
  }

  const constProps: ConstProps = {
    modalKeyPrefix: DATA_MODS_MODAL_KEY_PREFIX,
    modalTitle: DATA_MODS_MODAL_TITLE,
    isCommentOptional: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_COMMENT_OPTIONAL,
    reasonCodes: _getReasonCodes,
    dataTestAttrs: {component: 'DataModificationsModal'},
    reasonCodeAndCommentType: ReasonCodeAndCommentType.DATA_MODIFICATIONS,
  };

  const combinedProps = {...props, ...constProps};

  return <ReasonCodeAndCommentModal {...combinedProps} />;
}
